import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { ButtonLink, Notifications, NotificationType } from 'venn-ui-kit';
import { analyticsService, Routes, useApi } from 'venn-utils';
import type { GeneralAnalysisTemplate } from 'venn-api';
import { deleteReportTemplate } from 'venn-api';
import { AnalysisContext, LaunchAnalysisTemplate, UserContext } from 'venn-components';
import TemplatesTable from './TemplatesTable';
import { FlexRow, FlexWrapper, Info, Name, StyledIcon } from '../shared';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';

const Templates = ({ history }: RouteComponentProps) => {
  const { hasPermission } = useContext(UserContext);
  const isMountedRef = useRef(false);
  const { templates, refreshTemplates } = useContext(AnalysisContext);
  const [launchAnalysisTemplateVisible, setLaunchAnalysisTemplateVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<GeneralAnalysisTemplate>();
  const abortableDeleteReportTemplate = useApi(deleteReportTemplate);
  const isReadOnly = !hasPermission('ANALYSIS_PAGE_CREATE_TEMPLATE');

  useLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const launchAnalysisModal = (template: GeneralAnalysisTemplate) => {
    analyticsService.ctaClicked({
      destination: undefined,
      text: template.name,
      purpose: `Select subject for ${template.name}`,
      type: 'display button',
      filled: false,
    });
    setSelectedTemplate(template);
    setLaunchAnalysisTemplateVisible(true);
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      await abortableDeleteReportTemplate(templateId);
      refreshTemplates();
    } catch (e) {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      if (e.name !== 'AbortError' && isMountedRef.current) {
        Notifications.notify('Unable to delete template.', NotificationType.ERROR);
      }
    }
  };

  const customTemplates = templates.filter((template) => template.analysisTemplateType === 'CUSTOM');
  return (
    <div className="rounded-[4px] border border-solid border-venn-grey mt-[20px]">
      <div className="py-[40px] px-[60px]">
        <FlexRow>
          <div>
            <FlexWrapper>
              <StyledIcon type="calculator" />
              <Name>Manage Analysis Templates</Name>
            </FlexWrapper>
          </div>
          {!isReadOnly && (
            <ButtonLink
              linkText="Create a New Template"
              to={Routes.CREATE_TEMPLATE}
              className="qa-create-template h-[40px] min-w-[170px]"
            />
          )}
        </FlexRow>
        <Info>Review, edit, and delete all of the custom analysis templates created by your organization.</Info>
      </div>
      {launchAnalysisTemplateVisible && selectedTemplate && (
        <LaunchAnalysisTemplate
          templateId={selectedTemplate.id}
          templateName={selectedTemplate.name}
          onCancel={() => setLaunchAnalysisTemplateVisible(false)}
        />
      )}
      {customTemplates.length !== 0 && (
        <TemplatesTable
          onDelete={isReadOnly ? undefined : ({ id }: GeneralAnalysisTemplate) => deleteTemplate(id)}
          onEdit={
            isReadOnly
              ? undefined
              : ({ id }: GeneralAnalysisTemplate) => {
                  history.push(`${Routes.EDIT_TEMPLATE_PATH}/${id}`);
                }
          }
          templates={customTemplates}
          launchAnalysisModal={launchAnalysisModal}
        />
      )}
    </div>
  );
};

export default withRouter(Templates);
